/** Use as: 
  <div data-video-wrapper>
    <youtube-video class="video-element video-element--landscape data-options='{
      "id":"{{ section.settings.video.id }}",
      "autoplay":{{ section.settings.autoplay_video }}
    }'>
      <div class="video-section__placeholder" data-video-trigger>
        <img src=""> // placeholder image
        <button class="video-element__button button-reset" aria-label="{%- render 'format-translation', namespace: 'products.product.media', key: 'play_video', fallback: 'Play video' -%}" data-video-play-button>
          {%- render 'global-icon', icon: 'play_button', icon_size: 'md' -%}
        </button>
      </div>
      <div id="ytplayer"></div>
    </youtube-video> 
    // optional, but make sure it's within 'data-video-wrapper'
    <img src="" data-video-overlay-image> <-- this is not the button, but an image or animation partially overlaying the video.
  </div>
*/

if (!customElements.get("youtube-video")) {
  class CustomYTVideo extends HTMLElement {
    constructor() {
      super();

      this.options = {
        id: "",
        autoplay: false,
        video_element_id: "ytplayer",
      };

      if (this?.dataset?.options) {
        const dataOptions = JSON.parse(this.dataset.options);
        this.options = {
          ...this.options,
          ...dataOptions,
        };
      }

      if (this.options.id === "") {
        debug() && console.error("youtube ID is missing");
        return;
      }

      // overwrite true/false with integer
      this.options.autoplay ? (this.options.autoplay = 1) : (this.options.autoplay = 0);
      this.options.autoplay ? (this.options.muted = 1) : (this.options.muted = 0);

      this.player;
      this.links = this.querySelectorAll("a");
      this.videoTriggers = this.querySelectorAll("[data-video-trigger]");
      this.videoWrapper = this.closest("[data-video-wrapper]");
      this.overlayImage = this.videoWrapper.querySelector("[data-video-overlay-image]");
      this.overlayMobImage = this.videoWrapper.querySelector("[data-video-overlay-image-mobile]");
      this.overlayText = this.videoWrapper.querySelector("[data-video-text-overlay]");
      window.playerIsReady = false;

      // catch click on links that should not trigger the video
      this.links?.forEach((link) =>
        link.addEventListener("click", function (event) {
          event.stopImmediatePropagation();
        }),
      );

      // init video
      this.loadVideo();

      this.debouncedOnLoad = debounce((event) => {
        this.videoObserver(event.type);
      }, 100);

      if (this.options.autoplay === 1) {
        // play video on scroll
        window.addEventListener("scroll", this.debouncedOnLoad.bind(this));

        // play video if it's visible on load
        window.addEventListener("load", this.debouncedOnLoad.bind(this));
      }
    }

    videoObserver(event) {
      // Register IntersectionObserver
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const elementBounds = entry.boundingClientRect;
          const isBottomVisible =
            !!(elementBounds.bottom < window.innerHeight) && elementBounds.bottom;
          const isTopVisible = !!(elementBounds.top > 0) && elementBounds.top;
          const isSlideLeft = !!elementBounds.left == 0;
          if (window.playerIsReady) {
            if (entry.target.closest(".swiper-slide") !== null) {
              if (isBottomVisible && isTopVisible && isSlideLeft) {
                this.playVideo(this.player);
              }
            } else if (isBottomVisible && isTopVisible) {
              this.playVideo(this.player);
            } else {
              this.pauseVideo(this.player);
            }
          } else {
            this.loadVideo();
          }
        });
      });
      if (this.videoWrapper.dataset.videoIsPaused !== "true") {
        observer.observe(this);
      }
    }

    loadVideo() {
      const self = this;

      window.onYouTubeIframeAPIReady = () => {
        const player = new YT.Player(self.options.video_element_id, {
          height: 675,
          width: 1200,
          videoId: self.options.id,
          playerVars: {
            playsinline: 0,
            rel: 0,
            autoplay: self.options.autoplay,
            mute: self.options.muted,
          },
          events: {
            onReady: onPlayerReady,
            onStateChange: onPlayerStateChange,
          },
        });

        function onPlayerStateChange(event) {
          if (event.data == 1) {
            self.videoWrapper.dataset.videoIsPaused = "false";
          }
          if (event.data == 2) {
            self.videoWrapper.dataset.videoIsPaused = "true";
            // if video is paused, show placeholder
            self.showPlayerElements();
          }
        }

        function onPlayerReady(event) {
          window.playerIsReady = true;
          self.player = player;

          self.videoTriggers?.forEach((trigger) =>
            trigger.addEventListener("click", function (event) {
              event.preventDefault();
              event.stopPropagation();
              self.playVideo(self.player);
            }),
          );

          // when autoplay is enabled, let observer determine if it should play
          if (self.options.autoplay) {
            self.videoObserver();
          }
        }
      };
    }

    playVideo(player) {
      player.playVideo();
      this.hidePlayerElements();
    }

    pauseVideo(player) {
      player.pauseVideo();
      this.showPlayerElements();
    }

    hidePlayerElements() {
      if (window.innerWidth <= parseInt(this.overlayMobImage?.dataset.bp)) {
        this.overlayMobImage?.classList.add("hidden");
      }
      this.videoTriggers[0]?.classList.add("hidden");
      this.overlayImage?.classList.add("hidden");
      this.overlayText?.classList.add("hidden");
    }

    showPlayerElements() {
      if (window.innerWidth <= parseInt(this.overlayMobImage?.dataset.bp)) {
        this.overlayMobImage?.classList.remove("hidden");
      }
      this.videoTriggers[0]?.classList.remove("hidden");
      this.overlayImage?.classList.remove("hidden");
      this.overlayText?.classList.remove("hidden");
    }
  }
  window.CustomYTVideo = CustomYTVideo;
  customElements.define("youtube-video", CustomYTVideo);
}
